import React, {useCallback, useContext, useState} from 'react';
import {useParams} from "react-router-dom";
import {store} from "store";
import {addDays, format, getMonth} from "date-fns";
import {byDate as eventsByDate, deleteOne, groupSubmissions} from "../api";
import CalendarViews from './CalendarViews';
import differenceInDays from "date-fns/differenceInCalendarDays";
import Button from "@mui/material/Button";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import GetAppIcon from '@mui/icons-material/GetApp';
import EventsForm from "../EventsForm";
import CalendarDateSelect from "../../molcules/CalendarDateSelect/CalendarDateSelect";
import DeleteDialog from "../../molcules/Dialogs/DeleteDialog";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {hasScope} from "../../helpers/scopes";
import {calendarSummary} from '../../Rotashifts/api';

const dayDateLabel = (inDate) => {
  return format(inDate, 'EEE, do MMM');
}

const toSafeCsvCell = (value) => {
  if (value === null || value === undefined) return '""';

  let stringValue = String(value);
  if (/^[=+\-@]/.test(stringValue)) {
    stringValue = `'${stringValue}`;
  }

  return `"${stringValue.replace(/"/g, '""')}"`;
};

const downloadCsvFile = (rows, filename) => {
  const csv = `\uFEFF${rows.join('\r\n')}`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

let fetchEvents = ({siteID, setState, dateRange, filters, urlQuery, groupsProfiles}) => {
  const updateTotals = (totalsDic, dateLabel, event) => {
    totalsDic[dateLabel] = totalsDic[dateLabel] || {
      kite: 0,
      events: 0,
      arrivals: 0,
      departures: 0,
      sleepers: 0,
      dayGuests: 0,
      extraActual: 0,
      extraForecast: 0,
      totalPrice: 0
    };

    totalsDic[dateLabel].events += 1;
    totalsDic[dateLabel].kite += parseInt(event.kite || 0);
    totalsDic[dateLabel].arrivals += parseInt(event.arrivals || 0);
    totalsDic[dateLabel].departures += parseInt(event.departures || 0);
    totalsDic[dateLabel].sleepers += parseInt(event.sleepers);
    totalsDic[dateLabel].dayGuests += parseInt(event.dayGuests);
    totalsDic[dateLabel].extraForecast += event.extraForecast;
    totalsDic[dateLabel].totalPrice += event.totalPrice;
  }

  const calculateKiteProfiles = (eventVal, groupProfiles) => {
    groupProfiles.forEach(group => {
      if (group._id.eventId === eventVal._id) {
        eventVal.kite = group.count;
      }
    })
  }

  return eventsByDate({
    from: format(dateRange[0], 'yyyy-MM-dd'),
    to: format(dateRange[1], 'yyyy-MM-dd'),
    site: siteID
  }).then(data => {
    let countOfProfiles = groupsProfiles.data;
    let dayDistance = differenceInDays(dateRange[1], dateRange[0]) + 1;
    const days = [...Array(dayDistance).keys()].map((val, index) => addDays(dateRange[0], index));
    let dateLabelDict = {};
    let dateLabelTotals = {};
    let events = data.data;
    if (events) {
      /** Build out all the days inbetween */
      days.forEach(day => {
        const dateLabel = dayDateLabel(day);
        dateLabelDict[dateLabel] = [];
      });

      data.data.map(event => {
        const startDate = new Date(event.startDate);
        let dayDistance = differenceInDays(new Date(event.endDate), startDate) + 1;
        // ignore negative dayDistance
        if (dayDistance > 0) {
          [...Array(dayDistance).keys()].forEach((val, index) => {
            const dateLabel = dayDateLabel(addDays(startDate, index));
            if (index === 0) {
              updateTotals(dateLabelTotals, dateLabel, event);
              calculateKiteProfiles(event, countOfProfiles);
            }
            if(dateLabelDict[dateLabel]) {
              dateLabelDict[dateLabel].push({...event, start: index === 0, end: index === dayDistance-1, dayDistance: dayDistance, day: index});
            }
          });
        }
        return event;
      });

      // Assign non-overlapping lanes per week row so multi-day spans stay aligned
      // while still allowing natural packing in each column.
      const dayKeys = Object.keys(dateLabelDict);
      for (let rowStart = 0; rowStart < dayKeys.length; rowStart += 7) {
        const rowDays = dayKeys.slice(rowStart, Math.min(rowStart + 7, dayKeys.length));
        const rowLength = rowDays.length;

        const firstSeenOrder = new Map();
        let firstSeenCounter = 0;
        const intervalsByEventId = new Map();

        rowDays.forEach((dayKey, dayIdx) => {
          dateLabelDict[dayKey].forEach(ev => {
            if (!ev || !ev._id) return;

            if (!firstSeenOrder.has(ev._id)) {
              firstSeenOrder.set(ev._id, firstSeenCounter++);
            }

            const existing = intervalsByEventId.get(ev._id);
            if (!existing) {
              intervalsByEventId.set(ev._id, {
                eventId: ev._id,
                startIdx: dayIdx,
                endIdx: dayIdx,
                order: firstSeenOrder.get(ev._id)
              });
            } else {
              existing.startIdx = Math.min(existing.startIdx, dayIdx);
              existing.endIdx = Math.max(existing.endIdx, dayIdx);
            }
          });
        });

        const intervals = Array.from(intervalsByEventId.values()).sort((a, b) => {
          if (a.startIdx !== b.startIdx) return a.startIdx - b.startIdx;
          if (a.endIdx !== b.endIdx) return a.endIdx - b.endIdx;
          return a.order - b.order;
        });

        const laneUsage = [];
        const laneMatrix = [];

        intervals.forEach(interval => {
          let laneIndex = 0;
          let placed = false;

          while (!placed) {
            if (!laneUsage[laneIndex]) {
              laneUsage[laneIndex] = Array(rowLength).fill(false);
              laneMatrix[laneIndex] = Array(rowLength).fill(null);
            }

            let conflicts = false;
            for (let dayIdx = interval.startIdx; dayIdx <= interval.endIdx; dayIdx += 1) {
              if (laneUsage[laneIndex][dayIdx]) {
                conflicts = true;
                break;
              }
            }

            if (!conflicts) {
              for (let dayIdx = interval.startIdx; dayIdx <= interval.endIdx; dayIdx += 1) {
                laneUsage[laneIndex][dayIdx] = true;
                laneMatrix[laneIndex][dayIdx] = interval.eventId;
              }
              placed = true;
            } else {
              laneIndex += 1;
            }
          }
        });

        const dayEventMaps = rowDays.map(dayKey => {
          const map = new Map();
          dateLabelDict[dayKey].forEach(ev => {
            map.set(ev._id, ev);
          });
          return map;
        });

        rowDays.forEach((dayKey, dayIdx) => {
          const normalized = laneMatrix.map((lane, laneIdx) => {
            const eventId = lane[dayIdx];
            if (!eventId) {
              return { _id: `spacer-${rowStart}-${dayIdx}-${laneIdx}`, spacer: true };
            }

            return dayEventMaps[dayIdx].get(eventId) || { _id: `spacer-${rowStart}-${dayIdx}-${laneIdx}`, spacer: true };
          });

          dateLabelDict[dayKey] = normalized;
        });
      }

      setState((prev) => {
        const events = Object.keys(dateLabelDict).map((key, index) => ({
          date: days[index],
          label: key,
          events: dateLabelDict[key],
          summary: dateLabelTotals[key],
          month: getMonth(days[index]),
          inCurrentMonth: getMonth(days[index]) === parseInt(urlQuery.month)
        }));
        return {
          ...prev,
          staffEditOpen: false,
          events: events
        }
      });
      return data.data;
    }
  });
}


export default function EventsTimeline() {
  const queryClient = useQueryClient();
  const {state: appState} = useContext(store);
  let { mode } = useParams();
  const [dateRange, setDateRange] = useState();
  const [showDelete, setShowDelete] = useState(false);
  const [selected, setSelected] = useState(false);
  const [filters] = useState({period: 'Month'});
  const [state, setState] = useState({
    staffEditOpen: false,
    create:false,
    selectedStaff:{},
    events: []
  });

  const canReadRota = hasScope(appState.user, 'rota-shifts:read') && hasScope(appState.user, 'absences:read');
  const {data: rota, isLoading: rotaLoading, isError: rotaError} = useQuery({
    queryKey: ['calendarRota', appState.site._id, dateRange, appState.user?._id],
    queryFn: () => calendarSummary({siteId: appState.site._id,
      startDate: format(dateRange[0], 'yyyy-MM-dd'), endDate: format(dateRange[1], 'yyyy-MM-dd')}),
    enabled: canReadRota && !!dateRange && !!appState.site._id,
    refetchOnWindowFocus: false
  });

  const {data: groupsProfiles} = useQuery({
    queryKey: [
      'groups',
      appState.site._id,
      dateRange,
      filters,
      appState.urlQuery
    ],
    queryFn: () => groupSubmissions({
      site: appState.site._id,
      group: 'eventId',
      fields: 'count'
    }),
    enabled: !!dateRange && !!appState?.site?._id,
    refetchOnWindowFocus: false
  });

  const {isLoading = true} = useQuery({
    queryKey: [
      'eventsTimeline',
      appState.site._id,
      dateRange,
      filters,
      appState.urlQuery,
      groupsProfiles
    ],
    queryFn: () => fetchEvents({
      siteID: appState.site._id,
      setState: setState,
      dateRange: dateRange,
      urlQuery: appState.urlQuery,
      filters: filters,
      groupsProfiles: groupsProfiles
    }),
    enabled: !!dateRange && !!appState?.site?._id && !!groupsProfiles,
    refetchOnWindowFocus: false
  });

  const handleCloseStaffEdit = () => {
    queryClient.invalidateQueries(['eventsTimeline']);
  };

  const datesChanged = useCallback(
    (newDates) => {
      setDateRange(newDates);
    },[]
  );

  const handleDelete = (e, event) => {
    if(e.stopPropagation) {
      e.stopPropagation();
    }
    e.preventDefault();
    setShowDelete(true);
    setSelected(event);
  };

  const deleteShift = () => {
    deleteOne(selected._id).then(() => {
      queryClient.invalidateQueries(['eventsTimeline']);
      setShowDelete(false);
    });
  }

  const exportAllEventsToCSV = () => {
    fetchEvents({
      siteID: appState.site._id,
      setState: ()=>{},
      dateRange: [dateRange[0], addDays(dateRange[1], 1130)], // set the date to 2 years in the future
      urlQuery: appState.urlQuery,
      filters: filters,
      groupsProfiles: groupsProfiles
    }).then(data => {
      const headers = ['Event Name', 'Start Date', 'End Date', 'Type', 'Company'];
      const rows = [headers.map(toSafeCsvCell).join(',')];

      (Array.isArray(data) ? data : []).forEach(event => {
        const eventRow = [
          event.groupName,
          event.startDate,
          event.endDate,
          event.type,
          event.company
        ];
        rows.push(eventRow.map(toSafeCsvCell).join(','));
      });

      downloadCsvFile(rows, `upcoming-events-${appState.site._id}.csv`);
    }).catch((error) => {
      console.error('Failed to export events CSV', error);
      window.alert('Unable to export CSV right now. Please try again.');
    });
  }

  return (<div>
      <CalendarViews mode={mode || 'month'} events={state.events} loading={isLoading}
        dateControls={<CalendarDateSelect onChange={datesChanged} compact />}
        actions={hasScope(appState.user, 'events:write') && <>
          <Button variant="outlined" color="primary" onClick={exportAllEventsToCSV}
            disabled={!dateRange || !groupsProfiles} startIcon={<GetAppIcon fontSize="small" />}>EXPORT</Button>
          <Button variant="outlined" color="primary" onClick={() => {
            setState(prev => {
              return {...prev, create: true, staffEditOpen: true}
            });
          }} startIcon={<AddCircleOutlineIcon fontSize="small" />}>ADD</Button>
        </>}
        rota={canReadRota ? {days: rota?.data || {}, loading: rotaLoading, error: rotaError} : undefined}
        onDelete={hasScope(appState.user, 'events:delete') ? handleDelete : undefined}
        edit={eventData => setState(prev => ({...prev, selectedStaff: eventData, staffEditOpen: true, create: false}))}
      />
      {hasScope(appState.user, 'events:delete') && <DeleteDialog
              heading={'Are you sure you want to delete this event?'}
              show={showDelete}
              onDelete={deleteShift}
              onClose={() => setShowDelete(false)}
      />}

      <EventsForm open={state.staffEditOpen}
                  user={state.selectedStaff}
                  create={state.create}
                  onClose={handleCloseStaffEdit} />

    </div>
  );
}
